/**
 * Read-only API client for Launcher V2 ("A2") against PR A1's frozen
 * `/api/tools/playbooks/*` read endpoints.
 *
 * `apiFetch` is injected (never imported/called internally) — same contract
 * as the v1 `mini-apps/launcher/api-client.ts` and precedented by
 * `mini-apps/launcher/__tests__/api-client-reauth.test.ts`. It is the
 * throwing mini-app boundary (`_shared/hooks/useApi.ts`, backed by
 * `_shared/fetch-error.ts`): on success it resolves with the raw `Response`
 * (callers keep their own `.json()` read); on any non-2xx it throws a
 * structured `FetchErrorLike` before control reaches the caller. NEVER pair
 * these wrappers with a manual `!res.ok` check — that check would be dead
 * code (see `mini-apps/CLAUDE.md`).
 *
 * Every wrapper parses its response body through the matching zod schema
 * from A1's `mini-apps/playbooks/schemas.ts` (imported, never redeclared —
 * `terra/no-parallel-schema-and-type`) and returns the inferred type. A
 * malformed payload fails loudly at the schema-parse boundary rather than
 * flowing an untyped shape into a consumer.
 */

import {
  PlaybooksArchiveIdentityResponseSchema,
  type PlaybooksArchiveIdentityResponse,
  PlaybooksCreateIdentityResponseSchema,
  type PlaybooksCreateIdentityResponse,
  PlaybooksCreatePlayResponseSchema,
  type PlaybooksCreatePlayRequest,
  type PlaybooksCreatePlayResponse,
  PlaybooksGenerateProgressEventSchema,
  type PlaybooksGenerateProgressEvent,
  PlaybooksGenerateSequenceCopyResponseSchema,
  type PlaybooksGenerateSequenceCopyRequest,
  type PlaybooksGenerateSequenceCopyResponse,
  PlaybooksGenerationContextResponseSchema,
  type PlaybooksGenerationContextResponse,
  type PlaybooksGenerationInputs,
  PlaybooksRenameIdentityResponseSchema,
  type PlaybooksRenameIdentityResponse,
  PlaybooksReorderRungsResponseSchema,
  type PlaybooksReorderRungsResponse,
  PlaybooksLibraryResponseSchema,
  type PlaybooksLibraryResponse,
  PlaybooksMatchedMembersLadderResponseSchema,
  type PlaybooksMatchedMembersLadderResponse,
  PlaybooksMatchedMembersResponseSchema,
  type PlaybooksMatchedMembersResponse,
  type PlaybooksMatchMembersRequest,
  PlaybooksSaveSegmentationConditionsResponseSchema,
  type PlaybooksSaveSegmentationConditionsResponse,
  PlaybooksSavePlaybookRungsResponseSchema,
  type PlaybooksSavePlaybookRungsResponse,
  PlaybooksSaveSequenceStepsResponseSchema,
  type PlaybooksSaveSequenceStepsResponse,
  PlaybooksSavePlaySequenceCopyResponseSchema,
  type PlaybooksSavePlaySequenceCopyResponse,
  PlaybooksSavePlayLadderResponseSchema,
  type PlaybooksSavePlayLadderResponse,
  PlaybooksForkIntoPlayResponseSchema,
  type PlaybooksForkIntoPlayRequest,
  PlaybooksUpdatePlaysResponseSchema,
  type PlaybooksUpdatePlaysRequest,
  type PlaybooksUpdatePlaysResponse,
  type PlaybooksSavePlaySequenceCopyRequest,
  type PlaybooksForkIntoPlayResponse,
  type PlaybooksClonePlayRequest,
  PlaybooksSetSequenceFlowResponseSchema,
  type PlaybooksSetSequenceFlowResponse,
  PlaybooksSetSequenceInflectionFieldsResponseSchema,
  type PlaybooksSetSequenceInflectionFieldsRequest,
  type PlaybooksSetSequenceInflectionFieldsResponse,
  PlaybooksSetSequenceSenderResponseSchema,
  type PlaybooksSetSequenceSenderResponse,
  PlaybooksSegmentationDetailResponseSchema,
  type PlaybooksSegmentationDetailResponse,
  PlaybooksSequenceDetailResponseSchema,
  type PlaybooksSequenceDetailResponse,
  PlaybooksSequenceVersionResponseSchema,
  type PlaybooksSequenceVersionResponse,
  PlaybooksPlaybookDetailResponseSchema,
  type PlaybooksPlaybookDetailResponse,
  PlaybooksPlaybookVersionResponseSchema,
  type PlaybooksPlaybookVersionResponse,
  PlaybooksPlaybookUsageResponseSchema,
  type PlaybooksPlaybookUsageResponse,
  PlaybooksUsageResponseSchema,
  type PlaybooksUsageResponse,
  PlaybooksArchivePlayResponseSchema,
  type PlaybooksArchivePlayResponse,
  PlaybooksWipePlayDispatchLogResponseSchema,
  PlaybooksRecheckDispatchResponseSchema,
  PlaybooksRetryDispatchResponseSchema,
  type PlaybooksRecheckDispatchRequest,
  type PlaybooksRecheckDispatchResponse,
  PlaybooksSentCopyResponseSchema,
  type PlaybooksSentCopyResponse,
  type PlaybooksRetryDispatchRequest,
  type PlaybooksRetryDispatchResponse,
  type PlaybooksWipePlayDispatchLogResponse,
  PlaybooksSendPlayResponseSchema,
  type PlaybooksSendPlayRequest,
  type PlaybooksSendPlayResponse,
  PlaybooksSettingsResponseSchema,
  type PlaybooksSettingsResponse,
  type PlaybooksUpdateSettingsRequest,
  PlaybooksPlaySendsResponseSchema,
  type PlaybooksPlaySendsResponse,
  PlaybooksSendLogPageResponseSchema,
  type PlaybooksSendLogPageQuery,
  type PlaybooksSendLogPageResponse,
  PlaybooksPlayLogPageResponseSchema,
  type PlaybooksPlayLogPageQuery,
  type PlaybooksPlayLogPageResponse,
  PlaybooksDispatchPayloadResponseSchema,
  type PlaybooksDispatchPayloadQuery,
  type PlaybooksDispatchPayloadResponse,
  PlaybooksPlaySummarySchema,
  type PlaybooksPlaySummary,
  PlaybooksPlaysResponseSchema,
  type PlaybooksPlaysResponse,
  PlaybooksPlaysLibraryResponseSchema,
  type PlaybooksPlaysLibraryResponse,
  type LauncherChannel,
  type SegmentationConditions,
  type SequenceSteps,
} from './schemas';
import type { AssembledDraftRung } from './playbook-draft';
import { parseSseChunk } from './sse';

/**
 * Core's `apiFetch` signature. A LOCAL alias on purpose (review-fix U11, N3
 * considered): importing core's `ApiFetchInit` — even type-only, through
 * `_shared/hooks/useApi` — drags `web/app-home/src/lib/*` into
 * `tsc -p tsconfig.tests.json` (every test imports this file), which cannot
 * compile it (`import.meta`, `@shared/*` paths). The same graph hazard
 * `__tests__/module-graph.test.ts` pins.
 */
type ApiFetch = (input: RequestInfo, init?: RequestInit & { timeoutMs?: number }) => Promise<Response>;

/**
 * `?playId=<encoded>` when `playId` is set, else `''` — the one query-string
 * rule the play-scoped reads share (plan 2026-09-25-002, U3, Decision 12).
 * Manual concatenation, not `URLSearchParams`, so a space encodes to `%20`
 * (same convention `play-flow-context.ts#flowContextPath` uses).
 */
function playIdQuery(playId: string | undefined): string {
  return playId === undefined ? '' : `?playId=${encodeURIComponent(playId)}`;
}

/**
 * Plan 2026-09-28-008 U2 (rulebook R5, R8, R14; Codex round-3 F3/F6),
 * TIGHTENED to a discriminated union by plan 2026-09-29-001 U1 (R3) — the
 * options every sequence SETTINGS write (sender, CC/reply-to, flow id,
 * rename) takes. `playId` is the ownership guard's query param; `group`
 * names the group the save was made from (`rungId`) and — when the rep was
 * shown a list — every group of this play they confirmed
 * (`confirmedRungIds`), so the server can refuse a write whose group no
 * longer names the sequence, or one that would now reach a group the rep
 * never saw.
 *
 * `group` is now REQUIRED whenever `playId` is given — the server 400s
 * `group_required` on an in-play write with no group (this used to be a
 * silent no-op, the exact shape a post-fork settings write fell through as).
 * `{}` (both fields absent) is still the Library-write shape. There is no
 * third shape (`playId` with no `group`) — it does not type-check.
 */
export type SequenceSettingsWriteOptions =
  | { playId?: undefined; group?: undefined }
  | { playId: string; group: { rungId: string; confirmedRungIds?: string[] } };

/** The request-body half of {@link SequenceSettingsWriteOptions} — `{}` when no group is named. */
function settingsGroupBody(group: SequenceSettingsWriteOptions['group']): Record<string, unknown> {
  if (group === undefined) return {};
  return group.confirmedRungIds === undefined
    ? { rungId: group.rungId }
    : { rungId: group.rungId, confirmedRungIds: group.confirmedRungIds };
}

/**
 * GET /api/tools/playbooks/library — segmentations/sequences/playbooks
 * head-version summaries. Library identities only; `opts.playId` (plan
 * 2026-09-25-002, U3) also lists that play's own segmentations and sequences.
 */
export async function getLibrary(
  apiFetch: ApiFetch,
  opts: { playId?: string } = {},
): Promise<PlaybooksLibraryResponse> {
  const res = await apiFetch(`/api/tools/playbooks/library${playIdQuery(opts.playId)}`);
  return PlaybooksLibraryResponseSchema.parse(await res.json());
}

/** GET /api/tools/playbooks/segmentations/:id — identity + head content + version history. */
export async function getSegmentation(
  apiFetch: ApiFetch,
  id: string,
): Promise<PlaybooksSegmentationDetailResponse> {
  const res = await apiFetch(`/api/tools/playbooks/segmentations/${encodeURIComponent(id)}`);
  return PlaybooksSegmentationDetailResponseSchema.parse(await res.json());
}

/** GET /api/tools/playbooks/sequences/:id — identity + head content + version history. */
export async function getSequence(
  apiFetch: ApiFetch,
  id: string,
): Promise<PlaybooksSequenceDetailResponse> {
  const res = await apiFetch(`/api/tools/playbooks/sequences/${encodeURIComponent(id)}`);
  return PlaybooksSequenceDetailResponseSchema.parse(await res.json());
}

/**
 * GET /api/tools/playbooks/sequences/:id/versions/:versionId — ONE concrete
 * sequence version, head or branch, addressed by its own version id.
 *
 * No current mapping ever points at a branch version (rung-level pins were
 * removed — see plans/2026-09-02-001-launcher-v2-rip-out-versioning-plan.md);
 * this endpoint stays for the version-history click-through.
 * `versionNumber` is `null` on a (now purely historical) branch row.
 */
export async function getSequenceVersion(
  apiFetch: ApiFetch,
  sequenceId: string,
  versionId: string,
): Promise<PlaybooksSequenceVersionResponse> {
  const res = await apiFetch(
    `/api/tools/playbooks/sequences/${encodeURIComponent(sequenceId)}/versions/${encodeURIComponent(versionId)}`,
  );
  return PlaybooksSequenceVersionResponseSchema.parse(await res.json());
}

/**
 * GET /api/tools/playbooks/playbooks/:id/versions/:versionId — ONE concrete
 * playbook version, head or branch, addressed by its own version id.
 *
 * No current play carries a version pin (removed — see
 * plans/2026-09-02-001-launcher-v2-rip-out-versioning-plan.md), so every play
 * renders `getPlaybook`'s `head`; this endpoint stays for the version-history
 * click-through.
 */
export async function getPlaybookVersion(
  apiFetch: ApiFetch,
  playbookId: string,
  versionId: string,
): Promise<PlaybooksPlaybookVersionResponse> {
  const res = await apiFetch(
    `/api/tools/playbooks/playbooks/${encodeURIComponent(playbookId)}/versions/${encodeURIComponent(versionId)}`,
  );
  return PlaybooksPlaybookVersionResponseSchema.parse(await res.json());
}

/**
 * GET /api/tools/playbooks/playbooks/:id — identity + head content + version
 * history. `opts.playId` (plan 2026-09-25-002, U3) serves that play's own
 * ladder as `head` instead of the template head.
 */
export async function getPlaybook(
  apiFetch: ApiFetch,
  id: string,
  opts: { playId?: string } = {},
): Promise<PlaybooksPlaybookDetailResponse> {
  const res = await apiFetch(`/api/tools/playbooks/playbooks/${encodeURIComponent(id)}${playIdQuery(opts.playId)}`);
  return PlaybooksPlaybookDetailResponseSchema.parse(await res.json());
}

/**
 * GET /api/tools/playbooks/playbooks/:id/usage — no A2 consumer yet (a real
 * "View usage" UI is Phase 2's divergence-aware tree, see the plan's Deferred
 * section); wrapper exists so the endpoint has a typed client entry point.
 */
export async function getPlaybookUsage(
  apiFetch: ApiFetch,
  id: string,
): Promise<PlaybooksPlaybookUsageResponse> {
  const res = await apiFetch(`/api/tools/playbooks/playbooks/${encodeURIComponent(id)}/usage`);
  return PlaybooksPlaybookUsageResponseSchema.parse(await res.json());
}

/**
 * GET /api/tools/playbooks/sequences/:id/usage — "where is this used"
 * (round-3-QA-fixes plan, decisions 1/2/6/9). Callers fetch this FRESH
 * (`staleTime: 0`) immediately before a save gate or the delete dialog
 * decides whether to show a list of places — never read from a cached
 * detail query, so the count on screen and the list on screen can never
 * disagree.
 */
export async function getSequenceUsage(
  apiFetch: ApiFetch,
  id: string,
): Promise<PlaybooksUsageResponse> {
  const res = await apiFetch(`/api/tools/playbooks/sequences/${encodeURIComponent(id)}/usage`);
  return PlaybooksUsageResponseSchema.parse(await res.json());
}

/** GET /api/tools/playbooks/segmentations/:id/usage — see `getSequenceUsage`. */
export async function getSegmentationUsage(
  apiFetch: ApiFetch,
  id: string,
): Promise<PlaybooksUsageResponse> {
  const res = await apiFetch(`/api/tools/playbooks/segmentations/${encodeURIComponent(id)}/usage`);
  return PlaybooksUsageResponseSchema.parse(await res.json());
}

// ─────────────────────────────────────────────────────────────────────────────
// Identity CRUD (PR B, U5 — Segmentations/Playbooks; PR C, U4B — Sequences)
//
// Same injected-`apiFetch` contract as the read wrappers above: a non-2xx has
// already thrown a `FetchErrorLike` before these return, so there is never an
// `!res.ok` check here.
//
// PR C CONTRACT (plan Key Technical Decision 7): PR C's "Save Playbook" /
// "Save & apply to campaigns" call `createVersion('playbook', …)` on the
// backend directly, NOT these endpoints. Their only dependency on this unit is
// that the identity exists and its `id` is stable — hence the deliberately
// minimal create response.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The three identity kinds exposed through the generic identity routes.
 * Sequence CRUD landed in PR C / U4A (server) + U4B (this file) — it follows
 * the exact same `renameEndpoint`/`duplicateRoute`/`archiveRoute` shape
 * segmentations and playbooks already used, which is why it slots into this
 * union rather than getting its own parallel set of wrapper functions.
 */
type CrudKind = 'segmentations' | 'playbooks' | 'sequences';

function identityPath(kind: CrudKind, id?: string, suffix?: string): string {
  const base = `/api/tools/playbooks/${kind}`;
  if (id === undefined) return base;
  return `${base}/${encodeURIComponent(id)}${suffix ?? ''}`;
}

const JSON_HEADERS = { 'Content-Type': 'application/json' } as const;

/**
 * Plan 2026-09-25-002, Key Technical Decision 13 — the play a new
 * segmentation / sequence (or a duplicate's copy) belongs to. Optional on
 * every create helper; sent only when set, so an omitting caller's wire body
 * is byte-identical to before.
 */
export interface OwningPlayOptions {
  owningPlayId?: string;
}

async function createIdentityRequest(
  apiFetch: ApiFetch,
  kind: CrudKind,
  name: string,
  description?: string | null,
  opts?: OwningPlayOptions,
): Promise<PlaybooksCreateIdentityResponse> {
  const res = await apiFetch(identityPath(kind), {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({
      name,
      ...(description === undefined ? {} : { description }),
      ...(opts?.owningPlayId === undefined ? {} : { owningPlayId: opts.owningPlayId }),
    }),
  });
  return PlaybooksCreateIdentityResponseSchema.parse(await res.json());
}

/**
 * POST /api/tools/playbooks/segmentations — new identity + an empty-conditions
 * v1. `opts.owningPlayId` makes it the play's own segmentation (hidden from the
 * Library, listed by `GET /library?playId=`).
 */
export function createSegmentationRequest(
  apiFetch: ApiFetch,
  name: string,
  opts?: OwningPlayOptions,
): Promise<PlaybooksCreateIdentityResponse> {
  return createIdentityRequest(apiFetch, 'segmentations', name, undefined, opts);
}

/**
 * POST /api/tools/playbooks/playbooks — new identity + an empty-ladder v1.
 * `description` is optional (omitted, the wire body carries only `name` and
 * the server stores `null`) — `CreatePlaySidebar`'s "Create new" leg is the
 * only caller that passes one.
 */
export function createPlaybookRequest(
  apiFetch: ApiFetch,
  name: string,
  description?: string | null,
): Promise<PlaybooksCreateIdentityResponse> {
  return createIdentityRequest(apiFetch, 'playbooks', name, description);
}

/**
 * POST /api/tools/playbooks/sequences — new identity + an empty-steps v1
 * (PR C, U4A/U4B). Not routed through {@link createIdentityRequest}: `channel`
 * is a required body field with no default (identity-INVARIANT — Key
 * Decision 5), which the two-arg generic helper has nowhere to carry.
 * `flowId` is never accepted here — the one-way lock has exactly one
 * gatekeeper, {@link setSequenceFlowRequest}.
 */
export async function createSequenceRequest(
  apiFetch: ApiFetch,
  name: string,
  channel: LauncherChannel,
  /**
   * Playbooks QA round 4, U9 (SN1) / Decision 3 — the sender to lock this new
   * sequence to, collected by `SequenceCreateFields`. `null` (or omitted)
   * leaves `default_sender` NULL, i.e. "Not set until edited".
   *
   * Sent ONLY when non-null, so an omitting caller's wire body stays
   * byte-identical to the pre-U9 `{ name, channel }`.
   */
  sender?: string | null,
  /** Plan 2026-09-25-002, Decision 13 — see {@link OwningPlayOptions}. */
  opts?: OwningPlayOptions,
): Promise<PlaybooksCreateIdentityResponse> {
  const res = await apiFetch(identityPath('sequences'), {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({
      name,
      channel,
      ...(sender == null ? {} : { sender }),
      ...(opts?.owningPlayId === undefined ? {} : { owningPlayId: opts.owningPlayId }),
    }),
  });
  return PlaybooksCreateIdentityResponseSchema.parse(await res.json());
}

/**
 * PATCH /api/tools/playbooks/sequences/:id/sender — U9 (SN1) / Decision 8.
 *
 * The sequence's own locked sender, not a rung's. `null` clears it back to
 * "Not set"; the sub-resource PATCH shape mirrors {@link setSequenceFlowRequest}
 * because both write version-INVARIANT identity columns that must not advance
 * the head.
 *
 * Copy-on-map semantics (Decision 11) mean this affects rungs mapped AFTER the
 * edit; already-mapped rungs keep their copied `mapping.sender`.
 */
export async function setSequenceSenderRequest(
  apiFetch: ApiFetch,
  sequenceId: string,
  sender: string | null,
  opts: SequenceSettingsWriteOptions = {},
): Promise<PlaybooksSetSequenceSenderResponse> {
  const res = await apiFetch(`${identityPath('sequences', sequenceId, '/sender')}${playIdQuery(opts.playId)}`, {
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify({ sender, ...settingsGroupBody(opts.group) }),
  });
  return PlaybooksSetSequenceSenderResponseSchema.parse(await res.json());
}

/**
 * PATCH /api/tools/playbooks/sequences/:id/inflection — playbooks-inflection-
 * wiring (2026-09-17). Both fields are written together (one popover edits
 * both); `null` clears either back to "Not set". Same identity-column-write
 * shape as {@link setSequenceSenderRequest} — no version bump, 400
 * `inflection_only` from the server on any non-Inflection channel.
 */
export async function setSequenceInflectionFieldsRequest(
  apiFetch: ApiFetch,
  sequenceId: string,
  fields: PlaybooksSetSequenceInflectionFieldsRequest,
  opts: SequenceSettingsWriteOptions = {},
): Promise<PlaybooksSetSequenceInflectionFieldsResponse> {
  const res = await apiFetch(`${identityPath('sequences', sequenceId, '/inflection')}${playIdQuery(opts.playId)}`, {
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify({ ...fields, ...settingsGroupBody(opts.group) }),
  });
  return PlaybooksSetSequenceInflectionFieldsResponseSchema.parse(await res.json());
}

/**
 * PATCH /api/tools/playbooks/{segmentations,playbooks}/:id — identity-row
 * rename. Produces NO version row, so a rename never advances the head version
 * number a library row displays.
 */
export async function renameIdentityRequest(
  apiFetch: ApiFetch,
  kind: CrudKind,
  id: string,
  patch: { name?: string; description?: string | null },
  opts: SequenceSettingsWriteOptions = {},
): Promise<PlaybooksRenameIdentityResponse> {
  const res = await apiFetch(`${identityPath(kind, id)}${playIdQuery(opts.playId)}`, {
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify({ ...patch, ...settingsGroupBody(opts.group) }),
  });
  return PlaybooksRenameIdentityResponseSchema.parse(await res.json());
}

/**
 * POST /api/tools/playbooks/{…}/:id/duplicate — no body by default: the
 * copy's name is derived server-side from the source identity's own row
 * (`"<name> (copy)"`), so the client can't drift from it.
 *
 * `body.steps` and `body.generationInputs` are both SEQUENCE-only
 * (round-2-followup plan, Unit B2, decision 3; U17/F6 for `generationInputs`):
 * when `steps` is provided, it seeds the new identity's v1 with those steps
 * instead of the source's current head steps, making "duplicate with an
 * edited copy already on it" one atomic server operation. When
 * `generationInputs` is provided, it carries the "Generate with AI"
 * provenance onto that same new identity — same shape "Update all"'s
 * ordinary save already threads through {@link saveSequenceStepsRequest}.
 * `body.expectedSourceVersionId` (U19, M1) is likewise SEQUENCE-only: the head
 * of the SOURCE the caller's `steps` were edited/generated against. The server
 * re-reads that head inside the create transaction and answers 409
 * `version_conflict` if it moved, so "Save a new version" can never fork a new
 * identity that pairs stale copy with the source's freshly-changed binding.
 *
 * `body.conditions` is the SEGMENTATION analogue of `steps` (Playbooks QA
 * round 4, U6/S2, Decision 13): the usage sidebar's "Save a new version" for a
 * shared segmentation forks the identity with the rep's DRAFT conditions
 * already on the fork's first version, one write, so the fork never exists
 * carrying the source's stale conditions. `expectedSourceVersionId` is shared
 * by both kinds (the server's own per-kind body schema is what rejects a field
 * sent to the wrong kind).
 *
 * All four are optional; the caller (`mutations.ts#duplicateSequence` /
 * `#duplicateSegmentation`) builds `body` with only the keys it was actually
 * given, so every existing 2-/3-/4-arg caller keeps sending byte-identical
 * wire bodies.
 */
export async function duplicateIdentityRequest(
  apiFetch: ApiFetch,
  kind: CrudKind,
  id: string,
  body?: {
    steps?: SequenceSteps;
    generationInputs?: PlaybooksGenerationInputs;
    conditions?: SegmentationConditions;
    expectedSourceVersionId?: string;
    /**
     * Playbooks QA round 6, U5 (item 3) — SEQUENCE-only, the "Save as a new
     * sequence" sender-fork override; see `PlaybooksDuplicateSequenceRequestSchema.sender`'s
     * own doc.
     */
    sender?: string | null;
    /**
     * Plan 2026-09-25-002, Decision 13 — both kinds: the copy belongs to this
     * play (see {@link OwningPlayOptions}).
     */
    owningPlayId?: string;
  },
): Promise<PlaybooksCreateIdentityResponse> {
  const res = await apiFetch(identityPath(kind, id, '/duplicate'), {
    method: 'POST',
    ...(body
      ? { headers: JSON_HEADERS, body: JSON.stringify(body) }
      : {}),
  });
  return PlaybooksCreateIdentityResponseSchema.parse(await res.json());
}

/**
 * DELETE /api/tools/playbooks/{…}/:id — a SOFT delete: the response carries
 * the `archivedAt` stamp, not a 204. `DELETE /v2/playbooks/:id` is additionally
 * Terra-admin-gated on the backend (R8) and throws a 403 `FetchErrorLike` for
 * anyone else.
 */
export async function archiveIdentityRequest(
  apiFetch: ApiFetch,
  kind: CrudKind,
  id: string,
): Promise<PlaybooksArchiveIdentityResponse> {
  const res = await apiFetch(identityPath(kind, id), { method: 'DELETE' });
  return PlaybooksArchiveIdentityResponseSchema.parse(await res.json());
}

/**
 * POST /api/tools/playbooks/playbooks/:id/reorder — persist the waterfall's
 * new rung order as a new MAIN-LINE playbook version.
 *
 * `orderedRungIds` are the stable `id`s of the ladder's non-catch-all rungs, in
 * their new priority order (the catch-all is pinned last and never enters the
 * drag). The server renumbers positions `0..n-1` and echoes the stored ladder
 * back in `rungs`, so a caller reconciles against that rather than predicting
 * the renumbering.
 *
 * `expectedVersionId` is the playbook version id the caller's ladder was
 * actually read from (`GET /v2/playbooks/:id`'s `head.versionId`) — the
 * server refuses with `version_conflict` (409) if the head moved since,
 * instead of silently writing a reorder computed from a stale ladder.
 */
export async function reorderPlaybookRungsRequest(
  apiFetch: ApiFetch,
  playbookId: string,
  orderedRungIds: string[],
  expectedVersionId: string,
): Promise<PlaybooksReorderRungsResponse> {
  const res = await apiFetch(identityPath('playbooks', playbookId, '/reorder'), {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ orderedRungIds, expectedVersionId }),
  });
  return PlaybooksReorderRungsResponseSchema.parse(await res.json());
}

/**
 * POST /api/tools/playbooks/playbooks/:id/save — the playbook-draft owner
 * (PR C, U3A/U3C). `rungs` is whatever `assembleDraftRungs` produced: `id`
 * present only where the draft rung is already persisted, `position` derived
 * from array order server-side. `position` is stripped here (rather than
 * relied on to be ignored) so the wire payload matches
 * `PlaybooksSaveRungSchema` exactly — that schema has no `position` field at
 * all (Decision 1: the array order IS the order).
 *
 * `applyToCampaignSfdcIds`, when non-empty, is the "Save & apply to
 * campaigns" leg on this SAME request (plan Decision: the save and the Plays
 * it creates must agree on which version they were created against).
 */
export async function savePlaybookRungsRequest(
  apiFetch: ApiFetch,
  playbookId: string,
  rungs: AssembledDraftRung[],
  expectedVersionId: string,
  applyToCampaignSfdcIds?: string[],
): Promise<PlaybooksSavePlaybookRungsResponse> {
  const body: Record<string, unknown> = {
    rungs: rungs.map(({ position: _position, ...rung }) => rung),
    expectedVersionId,
  };
  if (applyToCampaignSfdcIds && applyToCampaignSfdcIds.length > 0) {
    body.applyToCampaignSfdcIds = applyToCampaignSfdcIds;
  }
  const res = await apiFetch(identityPath('playbooks', playbookId, '/save'), {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(body),
  });
  return PlaybooksSavePlaybookRungsResponseSchema.parse(await res.json());
}

/**
 * POST /api/tools/playbooks/plays/:id/ladder — the play's WHOLE ladder (plan
 * 2026-09-25-002, U4, Decisions 5, 6, 16): the play-mode counterpart of
 * {@link savePlaybookRungsRequest}, used for every ladder edit and reorder in
 * a play. Same wire rungs (position stripped; array order is the order).
 * `expectedVersionId` is the ladder the caller read — the play's pin
 * (`GET /playbooks/:id?playId=`'s `head.versionId`). The server never forks
 * here and never substitutes ids: every posted id is written as-is (plan
 * 2026-09-27-006 Decision 2; plan 2026-09-28-006 U1, rulebook R11); adopt the
 * response's `rungs`.
 */
export async function savePlayLadderRequest(
  apiFetch: ApiFetch,
  playId: string,
  rungs: AssembledDraftRung[],
  expectedVersionId: string,
): Promise<PlaybooksSavePlayLadderResponse> {
  const res = await apiFetch(`/api/tools/playbooks/plays/${encodeURIComponent(playId)}/ladder`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({
      rungs: rungs.map(({ position: _position, ...rung }) => rung),
      expectedVersionId,
    }),
  });
  return PlaybooksSavePlayLadderResponseSchema.parse(await res.json());
}

/**
 * POST /api/tools/playbooks/plays/:id/fork — give ONE group (`rungId`,
 * required) its own NEW copy of the segmentation / sequence it points at — a
 * Library piece or one of this play's own copies (plan 2026-09-27-006 U3.3,
 * Decisions 1, 11; plan 2026-09-28-006 U1, rulebook R3). `expectedSourceVersionId`
 * (optional) is the source head the caller composed against; a moved head is
 * a 409. Every call makes a fresh copy; adopt `ladder` (only that rung moved).
 */
export async function forkIntoPlayRequest(
  apiFetch: ApiFetch,
  playId: string,
  body: PlaybooksForkIntoPlayRequest,
): Promise<PlaybooksForkIntoPlayResponse> {
  const res = await apiFetch(`/api/tools/playbooks/plays/${encodeURIComponent(playId)}/fork`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(body),
  });
  return PlaybooksForkIntoPlayResponseSchema.parse(await res.json());
}

/**
 * POST /api/tools/playbooks/plays/:id/update-plays — option ② of the in-play
 * Save menu (plan 2026-09-28-006 U1 wire; the endpoint lands in U2; rulebook
 * R8): apply one change to every unsent play still sharing a Library piece,
 * each affected group getting its own copy; the Library is never written.
 * `body.confirmed` names the set the rep was shown — the origin group and each
 * unsent play with its groups (`usage-sidebar-logic.ts#confirmedFanOutSet`);
 * the server refuses any difference. A 409 `shared_mapping_conflict` means
 * the list changed under the rep; a 409 `version_conflict` means the Library
 * piece moved or the origin group no longer uses it. The response's `steps`
 * is the normalized form the copies were written with (`null` for a settings
 * change) — cache that, not what was posted. Session router only.
 */
export async function updatePlaysRequest(
  apiFetch: ApiFetch,
  playId: string,
  body: PlaybooksUpdatePlaysRequest,
): Promise<PlaybooksUpdatePlaysResponse> {
  const res = await apiFetch(`/api/tools/playbooks/plays/${encodeURIComponent(playId)}/update-plays`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(body),
  });
  return PlaybooksUpdatePlaysResponseSchema.parse(await res.json());
}

/**
 * POST /api/tools/playbooks/plays/:id/clone — the Review step's Apply (plan
 * 2026-09-27-006 U3.4, Decision 7): a new draft play carrying this play's
 * edits as its own copies. `expectedVersionId` is required — the play-scoped
 * ladder the rep reviewed; a moved pin answers 409 `version_conflict` (plan
 * 2026-09-27-007 Key Decision 6). `campaignSfdcId` / `name` are optional
 * (default: the source's). Answers the create envelope.
 */
export async function clonePlayRequest(
  apiFetch: ApiFetch,
  playId: string,
  body: PlaybooksClonePlayRequest,
): Promise<PlaybooksCreatePlayResponse> {
  const res = await apiFetch(`/api/tools/playbooks/plays/${encodeURIComponent(playId)}/clone`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(body),
  });
  return PlaybooksCreatePlayResponseSchema.parse(await res.json());
}

/**
 * POST /api/tools/playbooks/plays — the new-Play route (Key Decision 1's
 * "new-Play" case). Requires a `playbookId`: a campaign can host several
 * plays pointing at different playbooks (Decision 14), so there is nothing to
 * infer it from.
 */
export async function createPlayRequest(
  apiFetch: ApiFetch,
  body: PlaybooksCreatePlayRequest,
): Promise<PlaybooksCreatePlayResponse> {
  const res = await apiFetch('/api/tools/playbooks/plays', {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(body),
  });
  return PlaybooksCreatePlayResponseSchema.parse(await res.json());
}

/**
 * PATCH /api/tools/playbooks/plays/:id — Playbooks QA round 4, U10 (P2).
 *
 * `null` CLEARS the name (back to the playbook-name fallback); a non-empty
 * string sets it. Responds with the full refreshed summary, not just the
 * name, because `updatedAt` is Prisma-maintained and `sendCount` is a
 * relation aggregate — neither is knowable client-side.
 */
export async function renamePlayRequest(
  apiFetch: ApiFetch,
  id: string,
  name: string | null,
): Promise<PlaybooksPlaySummary> {
  const res = await apiFetch(`/api/tools/playbooks/plays/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify({ name }),
  });
  return PlaybooksPlaySummarySchema.parse(await res.json());
}

/**
 * DELETE /api/tools/playbooks/plays/:id — U10 (P1). A SOFT archive: the
 * response carries the `archivedAt` stamp, not a 204, and the row survives so
 * any `playbooks_play_send` provenance pointing at it still resolves.
 *
 * Unlike `archiveIdentityRequest`, this is NOT admin-gated (Decision 7 — any
 * authenticated Playbooks user). A second delete answers 409.
 */
export async function archivePlayRequest(
  apiFetch: ApiFetch,
  id: string,
): Promise<PlaybooksArchivePlayResponse> {
  const res = await apiFetch(`/api/tools/playbooks/plays/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  return PlaybooksArchivePlayResponseSchema.parse(await res.json());
}

/**
 * DELETE /api/tools/playbooks/plays/:id/dispatch-log (plan
 * `playbooks-clay-wire-gaps`, U5b client for U5's `wipePlayDispatchLogEndpoint`)
 * — wipes every `launcher_dispatch_log` row this play ever wrote (real sends
 * AND test sends). Never touches `playbooks_play_send` — that table is an
 * immutable audit trail, untouched by this call.
 *
 * `launcher_admin`-gated, AUTH-scoped, server-side (`requireRole('launcher_admin',
 * 'auth')` — see `api.ts`'s route registration); this wrapper carries no gate
 * of its own, same as every other admin-only wrapper here (`updatePlaybooksSettings`).
 * 409s (a send still in flight) and 404s (no such play) both arrive as a
 * thrown `MiniAppFetchError` via the shared `apiFetch` boundary — never a raw
 * status code, its `userMessage`/`message` carries the server's `problem()`
 * `detail`/`title`.
 */
export async function wipePlayDispatchLogRequest(
  apiFetch: ApiFetch,
  playId: string,
): Promise<PlaybooksWipePlayDispatchLogResponse> {
  const res = await apiFetch(
    `/api/tools/playbooks/plays/${encodeURIComponent(playId)}/dispatch-log`,
    { method: 'DELETE' },
  );
  return PlaybooksWipePlayDispatchLogResponseSchema.parse(await res.json());
}

/**
 * POST /api/tools/playbooks/plays/:id/dispatch-log/retry (plan
 * 2026-09-26-003, U4; the refusal arm sharing rules (m)) — retry one send's
 * failed STARTs and the sends Clay stopped before delivery: `logIds` for a
 * row's own "Retry", omitted for "Retry all stopped or failed". A failed START
 * is re-sent only after Clay confirms it has no run for that member; a stop is
 * rebuilt from the play as it is now and never asks that question. Each row's
 * `outcome` says what happened (see `PlaybooksRetryDispatchOutcomeSchema`).
 *
 * Rep-facing (the router-level gate — anyone who can send the play). A
 * refusal (404: the send is not this play's; 400: a malformed body) arrives
 * as a thrown `MiniAppFetchError` whose `userMessage` is the server's own
 * plain-English `problem()` detail — show that, never a status code.
 */
export async function retryDispatchRequest(
  apiFetch: ApiFetch,
  playId: string,
  body: PlaybooksRetryDispatchRequest,
): Promise<PlaybooksRetryDispatchResponse> {
  const res = await apiFetch(
    `/api/tools/playbooks/plays/${encodeURIComponent(playId)}/dispatch-log/retry`,
    {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(body),
    },
  );
  return PlaybooksRetryDispatchResponseSchema.parse(await res.json());
}

/**
 * GET /api/tools/playbooks/plays/:id/dispatch-log/:logId/sent-copy (plan
 * 2026-09-27-003, U5) — "What was sent" for one row. `bodyHtml` is untrusted:
 * render it only through `SanitizedHtml`.
 */
export async function fetchSentCopyRequest(
  apiFetch: ApiFetch,
  playId: string,
  logId: string,
): Promise<PlaybooksSentCopyResponse> {
  const res = await apiFetch(
    `/api/tools/playbooks/plays/${encodeURIComponent(playId)}/dispatch-log/${encodeURIComponent(logId)}/sent-copy`,
  );
  return PlaybooksSentCopyResponseSchema.parse(await res.json());
}

/**
 * POST /api/tools/playbooks/plays/:id/dispatch-log/recheck (plan
 * 2026-09-27-003, U4) — re-ask Clay about one send's stuck rows. Read-and-merge
 * only; never re-sends. Throws the server's own `problem()` message on refusal.
 */
export async function recheckDispatchRequest(
  apiFetch: ApiFetch,
  playId: string,
  body: PlaybooksRecheckDispatchRequest,
): Promise<PlaybooksRecheckDispatchResponse> {
  const res = await apiFetch(
    `/api/tools/playbooks/plays/${encodeURIComponent(playId)}/dispatch-log/recheck`,
    {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(body),
    },
  );
  return PlaybooksRecheckDispatchResponseSchema.parse(await res.json());
}

/**
 * POST /api/tools/playbooks/plays/:id/reset (plan `playbooks-clay-wire-gaps`,
 * U5b client for U5's `resetPlayEndpoint`) — resets a `launched` play back to
 * `draft` so it can be re-launched, WITHOUT touching a single
 * `playbooks_play_send` row (those stay exactly as sent). Answers the full
 * refreshed `PlaybooksPlaySummary`, `status: 'draft'`.
 *
 * `launcher_admin`-gated, AUTH-scoped, server-side, same as
 * {@link wipePlayDispatchLogRequest}. 409s (archived, or already draft) and
 * 404s (no such play) both arrive as a thrown `MiniAppFetchError`.
 */
export async function resetPlayRequest(
  apiFetch: ApiFetch,
  playId: string,
): Promise<PlaybooksPlaySummary> {
  const res = await apiFetch(`/api/tools/playbooks/plays/${encodeURIComponent(playId)}/reset`, {
    method: 'POST',
  });
  return PlaybooksPlaySummarySchema.parse(await res.json());
}

/**
 * How long the browser waits for a send to be ACCEPTED (plan 2026-10-14-002
 * U0a). The server now answers in about a second whatever the roster size —
 * it only freezes the roster and writes the queued rows — but core's
 * `apiFetch` gives up at 15 s by default, and on 2026-10-14 exactly that
 * abort made a rep press Send twice on a send that had worked. A deliberate
 * 60 s is a safety margin, not an expectation; an abort past it is still
 * handled (`review-step-logic.ts#sendFailureOf`).
 */
export const SEND_REQUEST_TIMEOUT_MS = 60_000;

/**
 * POST /api/tools/playbooks/plays/:id/send — a send, real or test (PR D, U5a
 * backend / U5b this client; `isTest` added by U4). `memberIds` is the routed
 * roster the rep reviewed — a fail-closed cross-check against the server's own
 * re-derived ladder, not the source of who gets a send; `isTest` is required
 * and has no default. See `PlaybooksSendPlayRequestSchema`'s doc comment for
 * both.
 *
 * Answers 202 with the two-phase receipt: `sendId` (the `playbooks_play_send`
 * row — `null` on a test send, which writes none) and `dispatchId` (the join
 * key into `launcher_dispatch_log`, which the U6b inspector reads by), plus the
 * `queued`/`startFailed` partition of the DISPATCHED entries and `defects`,
 * the members skipped for a copy defect.
 */
export async function sendPlayRequest(
  apiFetch: ApiFetch,
  playId: string,
  body: PlaybooksSendPlayRequest,
): Promise<PlaybooksSendPlayResponse> {
  const res = await apiFetch(`/api/tools/playbooks/plays/${encodeURIComponent(playId)}/send`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(body),
    timeoutMs: SEND_REQUEST_TIMEOUT_MS,
  });
  return PlaybooksSendPlayResponseSchema.parse(await res.json());
}

/**
 * GET /api/tools/playbooks/plays/:id/sends — the play's send history, each
 * send with its server-computed `summary` (PR D, U6a backend / U6b this client
 * + the `DispatchInspector` it feeds). Read-only. The per-person rows are NOT
 * in this response (U0a) — see {@link listSendLogPageRequest}.
 */
export async function listPlaySendsRequest(
  apiFetch: ApiFetch,
  playId: string,
): Promise<PlaybooksPlaySendsResponse> {
  const res = await apiFetch(`/api/tools/playbooks/plays/${encodeURIComponent(playId)}/sends`);
  return PlaybooksPlaySendsResponseSchema.parse(await res.json());
}

/**
 * GET /api/tools/playbooks/plays/:id/sends/:dispatchId/log — one page of one
 * send's rows (plan 2026-10-14-002 U0b(d), client U0a). `status` is filtered
 * SERVER-side through the same classifier the chips count with; `after` is the
 * previous page's `nextAfter`.
 */
export async function listSendLogPageRequest(
  apiFetch: ApiFetch,
  playId: string,
  dispatchId: string,
  query: PlaybooksSendLogPageQuery,
): Promise<PlaybooksSendLogPageResponse> {
  const params = new URLSearchParams({ status: query.status });
  if (query.after !== undefined) params.set('after', query.after);
  params.set('limit', String(query.limit));
  const res = await apiFetch(
    `/api/tools/playbooks/plays/${encodeURIComponent(playId)}/sends/${encodeURIComponent(dispatchId)}/log?${params.toString()}`,
  );
  return PlaybooksSendLogPageResponseSchema.parse(await res.json());
}

/**
 * GET /api/tools/playbooks/plays/:id/log — one page of the play's rows across
 * EVERY send, tests and real, newest first (plan 2026-10-15-004 B1; the
 * Dispatch log's "All people" view). `kind` / `status` / `memberId` filter on
 * the server (`status` through the one classifier); `after` is the previous
 * page's `nextAfter`, an opaque cursor. Keyed by
 * `dispatch-inspector-logic.ts#playLogQueryKey`.
 */
export async function listPlayLogPageRequest(
  apiFetch: ApiFetch,
  playId: string,
  query: PlaybooksPlayLogPageQuery,
): Promise<PlaybooksPlayLogPageResponse> {
  const params = new URLSearchParams({ kind: query.kind, status: query.status });
  if (query.memberId !== undefined) params.set('memberId', query.memberId);
  if (query.after !== undefined) params.set('after', query.after);
  params.set('limit', String(query.limit));
  const res = await apiFetch(`/api/tools/playbooks/plays/${encodeURIComponent(playId)}/log?${params.toString()}`);
  return PlaybooksPlayLogPageResponseSchema.parse(await res.json());
}

/**
 * GET /api/tools/playbooks/plays/:id/dispatch-payload?memberId=…[&sendId=…]
 * — one member's Clay payload, REBUILT by the same pure builder the send
 * used (PR D, U6a backend / U6b this client). `sendId` reconstructs from
 * that send's LOCK ("what was posted"); omitting it reconstructs from the
 * current heads ("what will be posted") — see
 * `PlaybooksDispatchPayloadResponseSchema`'s doc comment for the
 * `reconstructed`/`note` caveat both modes carry.
 */
export async function previewDispatchPayloadRequest(
  apiFetch: ApiFetch,
  playId: string,
  query: PlaybooksDispatchPayloadQuery,
): Promise<PlaybooksDispatchPayloadResponse> {
  const search = new URLSearchParams({ memberId: query.memberId });
  if (query.sendId) search.set('sendId', query.sendId);
  const res = await apiFetch(
    `/api/tools/playbooks/plays/${encodeURIComponent(playId)}/dispatch-payload?${search.toString()}`,
  );
  return PlaybooksDispatchPayloadResponseSchema.parse(await res.json());
}

/**
 * POST /api/tools/playbooks/segmentations/:id/versions — a segmentation's
 * conditions, whole-snapshot (PR C, U3C). This is a LIBRARY-OBJECT edit (the
 * Qualify step's "Edit conditions" kebab item calls it directly and
 * immediately), not a playbook-draft edit — see the schema's own doc comment
 * for why `default_mapping` is not accepted here.
 */
export async function saveSegmentationConditionsRequest(
  apiFetch: ApiFetch,
  segmentationId: string,
  conditions: SegmentationConditions,
  expectedVersionId: string,
  /**
   * Playbooks QA round 4, U6 (S2) — how many places the rep was shown before
   * approving this save. Omitted (rather than defaulted to a number here) by
   * callers that predate the gate, so the server applies its own documented
   * `?? 1` sole-use default and the wire body stays byte-identical to before.
   */
  confirmedPlaceCount?: number,
  /**
   * Plan 2026-09-25-002, U7 (Decision 21) — the ownership guard's query
   * param; TIGHTENED to a union by plan 2026-09-29-001 U1 (R3) — `group` is
   * now required whenever `playId` is given (the server 400s
   * `group_required` on an in-play save with no group). Omitted in the
   * playbook doorway; the play flow's conditions save passes the play and
   * group addressing this identity.
   */
  opts: SequenceSettingsWriteOptions = {},
): Promise<PlaybooksSaveSegmentationConditionsResponse> {
  const res = await apiFetch(`${identityPath('segmentations', segmentationId, '/versions')}${playIdQuery(opts.playId)}`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({
      conditions,
      expectedVersionId,
      ...(confirmedPlaceCount === undefined ? {} : { confirmedPlaceCount }),
      ...settingsGroupBody(opts.group),
    }),
  });
  return PlaybooksSaveSegmentationConditionsResponseSchema.parse(await res.json());
}

/**
 * POST /api/tools/playbooks/sequences/:id/versions — a whole-snapshot copy
 * save (PR C, U4A/U4B). `steps` is the COMPLETE array, never a delta — see
 * `PlaybooksSaveSequenceStepsRequestSchema`'s doc comment; the caller
 * (`StepEditorShell` via `SequenceEditorShell`) is responsible for merging its
 * one edited step into the sequence's other steps before calling this.
 * `expectedVersionId` carries the identical lost-update contract as
 * {@link savePlaybookRungsRequest} / {@link reorderPlaybookRungsRequest} — a
 * stale value throws a 409 `version_conflict` `FetchErrorLike`.
 *
 * `confirmedMappingCount`, when passed, is the shared-save acknowledgment
 * (round-2-followup plan, Unit B2, decision 1) — the active-mapping count the
 * caller displayed to the rep before this save. Omitted, the server defaults
 * to `1` (the no-dialog sole-use save path). A count that grew past what was
 * acknowledged throws a 409 `shared_mapping_conflict` `FetchErrorLike`.
 *
 * `generationInputs`, when passed (U7a), carries the "Generate with AI"
 * provenance that produced `steps` — additive, omitted by every caller that
 * predates AI Apply routing through this ordinary save.
 *
 * `confirmedUnsentPlayCount`, when passed (round-4 QA fixes, U5/S1), is the
 * UNSENT-PLAY counterpart of `confirmedMappingCount` — the distinct-playbook
 * unsent-play count (`usage-gate.ts#distinctUnsentPlays`) the caller
 * displayed to the rep before this save. Omitted, the server defaults to `1`
 * (the no-dialog sole-use save path), same as `confirmedMappingCount`. A
 * count that grew past what was acknowledged throws the SAME 409
 * `shared_mapping_conflict` `FetchErrorLike`.
 */
export async function saveSequenceStepsRequest(
  apiFetch: ApiFetch,
  sequenceId: string,
  steps: SequenceSteps,
  expectedVersionId: string,
  confirmedMappingCount?: number,
  generationInputs?: PlaybooksGenerationInputs,
  confirmedUnsentPlayCount?: number,
  /**
   * Plan 2026-09-25-002, U7 (Decision 21) — the ownership guard's query
   * param; TIGHTENED to a union by plan 2026-09-29-001 U1 (R3) — `group` is
   * now required whenever `playId` is given. Omitted in the playbook
   * doorway; the play flow's editor save, "Add first step" and the Gong
   * import/rebind paths pass the play and group addressing this identity.
   */
  opts: SequenceSettingsWriteOptions = {},
): Promise<PlaybooksSaveSequenceStepsResponse> {
  const res = await apiFetch(`${identityPath('sequences', sequenceId, '/versions')}${playIdQuery(opts.playId)}`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({
      steps,
      expectedVersionId,
      ...(confirmedMappingCount !== undefined ? { confirmedMappingCount } : {}),
      ...(generationInputs !== undefined ? { generationInputs } : {}),
      ...(confirmedUnsentPlayCount !== undefined ? { confirmedUnsentPlayCount } : {}),
      ...settingsGroupBody(opts.group),
    }),
  });
  return PlaybooksSaveSequenceStepsResponseSchema.parse(await res.json());
}

/**
 * POST /api/tools/playbooks/plays/:playId/sequences/:sequenceId/copy — U4
 * (Decision 6). Saves the play's own whole-sequence copy: a main-line version
 * on the play-owned sequence, never the template (plan 2026-09-25-002,
 * Decision 7). `expectedVersionId` is REQUIRED on this wrapper (unlike the
 * request schema, which allows omitting it) and is always a string: the head
 * of the play's own sequence the play flow rendered
 * (`SequencesStep#savePlayCopy` sends `renderedVersion.versionId`, plan U6).
 * There is no `null` form (the server answers 400). A machine caller (the chat skill) that wants
 * "last write wins" calls the underlying endpoint directly without it.
 *
 * `generationInputs` is additive, same omit-when-undefined convention as
 * {@link saveSequenceStepsRequest}'s.
 *
 * `options` (plan 2026-09-28-006 U1, rulebook R3; `rungId` REQUIRED by plan
 * 2026-09-29-001 U1 — the session mount now 400s `group_required` on a body
 * with none): `rungId` names the group the save is for and `mode` says
 * whether an owned copy is written in place (`'ensure'`) or copied again for
 * that group (`'fork'`, "Save as new — this group only"). `mode` is omitted
 * when undefined (the server defaults to `'ensure'`). `confirmedRungIds`
 * (R8) is what an owned "Update N groups in this play" names: the groups of
 * this play the rep was shown; the server refuses `shared_mapping_conflict`
 * if another group joined, and echoes the set in `movedRungIds`. There is no
 * caller of THIS wrapper that omits `rungId` any more — a machine caller
 * (the chat skill) that wants the all-groups shape calls the underlying
 * endpoint directly, which this wrapper is not.
 */
export async function savePlaySequenceCopyRequest(
  apiFetch: ApiFetch,
  playId: string,
  sequenceId: string,
  steps: SequenceSteps,
  expectedVersionId: string,
  // No `?` — TS forbids a required parameter (the now-mandatory `options`
  // below) from following an optional one. Every caller must pass this slot
  // explicitly (`undefined` is fine and the wire body still omits the key).
  generationInputs: PlaybooksGenerationInputs | undefined,
  options: Pick<PlaybooksSavePlaySequenceCopyRequest, 'mode' | 'confirmedRungIds'> & { rungId: string },
): Promise<PlaybooksSavePlaySequenceCopyResponse> {
  const res = await apiFetch(
    `/api/tools/playbooks/plays/${encodeURIComponent(playId)}/sequences/${encodeURIComponent(sequenceId)}/copy`,
    {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({
        steps,
        expectedVersionId,
        rungId: options.rungId,
        ...(generationInputs !== undefined ? { generationInputs } : {}),
        ...(options.mode !== undefined ? { mode: options.mode } : {}),
        ...(options.confirmedRungIds !== undefined ? { confirmedRungIds: options.confirmedRungIds } : {}),
      }),
    },
  );
  return PlaybooksSavePlaySequenceCopyResponseSchema.parse(await res.json());
}

/**
 * PATCH /api/tools/playbooks/sequences/:id/flow — the flow bind (PR C,
 * U4A/U4B), REBINDABLE as of Batch 6 (D4 repealed the one-way lock). There is
 * still no `flowId: null` to unset. Re-sending the flow already bound is
 * idempotent (200, no new write); sending a DIFFERENT flow than the one
 * already bound now rebinds it (200, the new binding) — the caller
 * (`SequenceMappingDrawer`'s Gong tab dispatch, D2) is responsible for
 * pairing a rebind with its own whole-snapshot step re-import in the same
 * action. A genuine concurrent race can still throw a `VersioningError`
 * (`version_conflict`, 409) through the `FetchErrorLike` boundary, telling
 * the caller to reload and pick again.
 *
 * Free-text on non-Gong channels; Gong still catalog-validated
 * (playbooks-inflection-wiring, 2026-09-17) — see `SequenceFlowIdPopover`.
 */
export async function setSequenceFlowRequest(
  apiFetch: ApiFetch,
  sequenceId: string,
  flowId: string,
  opts: SequenceSettingsWriteOptions = {},
): Promise<PlaybooksSetSequenceFlowResponse> {
  const res = await apiFetch(`${identityPath('sequences', sequenceId, '/flow')}${playIdQuery(opts.playId)}`, {
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify({ flowId, ...settingsGroupBody(opts.group) }),
  });
  return PlaybooksSetSequenceFlowResponseSchema.parse(await res.json());
}

/**
 * POST /api/tools/playbooks/sequences/:id/generate-copy — AI copy generation
 * for one sequence (U12A, R16).
 *
 * WRITES NOTHING. It returns a generated `SequenceSteps` draft plus the
 * `headVersionId` that draft was generated against; accepting it is a separate,
 * ordinary {@link saveSequenceStepsRequest} call passing that same id as
 * `expectedVersionId`. That is deliberate — generated copy goes through the one
 * version write path with the one lost-update guard, exactly like hand-typed
 * copy, rather than through a second LLM-shaped door.
 *
 * Every request field is optional except `campaignSfdcId`, and every omission
 * has a real server-side source rather than a guessed default (see
 * `PlaybooksGenerateSequenceCopyRequestSchema`): the campaign row's own
 * `description`, "no source material", "no status filter", and the sequence's
 * saved head steps respectively.
 *
 * A model or transport failure is a 502 — the request was well-formed and the
 * upstream did not deliver — which reaches the caller as a thrown
 * `FetchErrorLike` whose `userMessage` is the server's `detail`. Callers that
 * want to present that themselves (rather than the generic global toast) pass
 * `meta: { suppressErrorToast: true }` on their `useMutation`.
 */
export async function generateSequenceCopyRequest(
  apiFetch: ApiFetch,
  sequenceId: string,
  body: PlaybooksGenerateSequenceCopyRequest,
): Promise<PlaybooksGenerateSequenceCopyResponse> {
  const res = await apiFetch(identityPath('sequences', sequenceId, '/generate-copy'), {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(body),
  });
  return PlaybooksGenerateSequenceCopyResponseSchema.parse(await res.json());
}

/** GET /api/tools/playbooks/sequences/:id/generation-context?campaignSfdcId=… — the drawer's stage-1 seed data (U4b). */
export async function getGenerationContext(
  apiFetch: ApiFetch,
  sequenceId: string,
  campaignSfdcId: string,
): Promise<PlaybooksGenerationContextResponse> {
  const search = new URLSearchParams({ campaignSfdcId });
  const res = await apiFetch(identityPath('sequences', sequenceId, `/generation-context?${search.toString()}`));
  return PlaybooksGenerationContextResponseSchema.parse(await res.json());
}

/** A `done` progress event, narrowed out of the discriminated union — what a completed generation resolves with. */
export type PlaybooksGenerateProgressDoneEvent = Extract<PlaybooksGenerateProgressEvent, { type: 'done' }>;
/** A `step` progress event's payload, narrowed out of the same union. */
type PlaybooksGenerateProgressStepEvent = Extract<PlaybooksGenerateProgressEvent, { type: 'step' }>;

export interface StreamSequenceCopyGenerationOptions {
  /** Invoked once per `step` SSE frame, in the order the server writes them. */
  onStep?: (step: PlaybooksGenerateProgressStepEvent['step']) => void;
  signal?: AbortSignal;
}

function newAbortError(): Error {
  const err = new Error('The operation was aborted.');
  err.name = 'AbortError';
  return err;
}

/** Rejects the moment `signal` aborts — raced against a pending `reader.read()` so an abort mid-stream doesn't hang. */
function abortRejection(signal: AbortSignal): Promise<never> {
  return new Promise((_resolve, reject) => {
    if (signal.aborted) {
      reject(newAbortError());
      return;
    }
    signal.addEventListener('abort', () => reject(newAbortError()), { once: true });
  });
}

/**
 * `POST /api/tools/playbooks/sequences/:id/generate-copy` over SSE (U4b).
 *
 * `apiFetch` is the SAME throwing wrapper every other `api-client.ts`
 * function uses — never `apiFetchRaw` — because every guard failure
 * (404/409/400) answers BEFORE the server ever sends SSE headers, so it is
 * still a normal non-2xx `apiFetch` throws on. Once headers ARE sent the
 * response is committed to 200 for the rest of the stream; a model/transport
 * failure shows up as an `error` frame, never a changed HTTP status.
 *
 * Falls back to the plain JSON envelope when the server didn't answer with
 * `text/event-stream` (e.g. streaming unavailable) — the caller gets the
 * same `done`-shaped result either way, so `GenerateWithAIDrawer` (U7) never
 * has to branch on transport.
 *
 * A frame that fails `PlaybooksGenerateProgressEventSchema.safeParse` is
 * skipped, never thrown (Codex-challenge resolution, U4b) — a single
 * malformed/incomplete frame must not fail the whole generation.
 */
export async function streamSequenceCopyGeneration(
  apiFetch: ApiFetch,
  sequenceId: string,
  body: PlaybooksGenerateSequenceCopyRequest,
  options: StreamSequenceCopyGenerationOptions = {},
): Promise<PlaybooksGenerateProgressDoneEvent> {
  const res = await apiFetch(identityPath('sequences', sequenceId, '/generate-copy'), {
    method: 'POST',
    headers: { ...JSON_HEADERS, Accept: 'text/event-stream' },
    body: JSON.stringify(body),
    signal: options.signal,
  });

  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('text/event-stream')) {
    const json = PlaybooksGenerateSequenceCopyResponseSchema.parse(await res.json());
    // D5 — transport-equivalent fallback: dispatch every step through
    // `onStep`, in position order, exactly as the SSE branch would have via
    // its `step` frames, before returning the `done` envelope. This is what
    // lets `finishCopyGeneration`'s completeness gate (every `positions`
    // entry has a staged step) hold identically on both transports.
    for (const step of json.steps) {
      options.onStep?.(step);
    }
    return {
      type: 'done',
      headVersionId: json.headVersionId,
      thinContext: json.thinContext,
      timingMs: json.timingMs,
      strategy: json.strategy,
      positions: json.steps.map((step) => step.position),
    };
  }

  const reader = res.body?.getReader();
  if (!reader) {
    throw new Error('No response stream');
  }

  const decoder = new TextDecoder();
  let buffer = '';
  // A8 — minted ONCE for the whole read loop (not per iteration): the old
  // per-iteration `abortRejection(options.signal)` call registered a NEW
  // `{ once: true }` abort listener on every `reader.read()` this raced
  // against, and every one of them stayed attached — none had fired — for
  // as long as the stream kept producing chunks without aborting. A long
  // multi-step generation could leave dozens of listeners on one signal.
  const abortPromise = options.signal ? abortRejection(options.signal) : null;
  let finishedNormally = false;
  try {
    while (true) {
      const { done, value } = abortPromise ? await Promise.race([reader.read(), abortPromise]) : await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      const { events, rest } = parseSseChunk(buffer, chunk);
      buffer = rest;

      for (const raw of events) {
        const parsed = PlaybooksGenerateProgressEventSchema.safeParse(raw);
        if (!parsed.success) continue;
        if (parsed.data.type === 'step') {
          options.onStep?.(parsed.data.step);
          continue;
        }
        if (parsed.data.type === 'error') {
          throw new Error(parsed.data.detail);
        }
        finishedNormally = true;
        return parsed.data;
      }
    }
    throw new Error('Generation stream ended without a done event.');
  } finally {
    // A8 — cancel on every exit path EXCEPT a normal `done` resolve: an
    // abort, an `error` frame, or the stream ending without a `done` event
    // all leave the underlying reader in a state the caller is walking away
    // from and must release; only the successful resolve leaves it
    // legitimately fully consumed.
    if (!finishedNormally) {
      await reader.cancel().catch(() => {});
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Matched members (PR B, U2)
//
// PR C CONTRACT (plan Key Technical Decision 7): `{ matchedMemberIds, sample,
// totalMatched }` is the frozen shape, and each ladder rung carries those same
// three fields. Additive changes only.
// ─────────────────────────────────────────────────────────────────────────────

const MATCH_PATH = '/api/tools/playbooks/campaign-members/match';

/**
 * GET …/campaign-members/match?campaignSfdcId&segmentationId — one saved
 * segmentation's matched members on a campaign, standalone (no ladder context,
 * so no first-match-wins exclusion is applied). An archived segmentation throws
 * a 409 `FetchErrorLike` rather than resolving to an empty match.
 */
export async function getSegmentationMatchedMembers(
  apiFetch: ApiFetch,
  params: { campaignSfdcId: string; segmentationId: string },
): Promise<PlaybooksMatchedMembersResponse> {
  const search = new URLSearchParams({
    campaignSfdcId: params.campaignSfdcId,
    segmentationId: params.segmentationId,
  });
  const res = await apiFetch(`${MATCH_PATH}?${search.toString()}`);
  return PlaybooksMatchedMembersResponseSchema.parse(await res.json());
}

/**
 * POST …/campaign-members/match — the Audience Rule's UNSAVED draft groups
 * (plan Decision 3). A POST only because filter groups are a nested object;
 * nothing is persisted, and "Save audience rule" is still Phase 1.
 */
export async function matchMembersByFilter(
  apiFetch: ApiFetch,
  body: PlaybooksMatchMembersRequest,
): Promise<PlaybooksMatchedMembersResponse> {
  const res = await apiFetch(MATCH_PATH, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(body),
  });
  return PlaybooksMatchedMembersResponseSchema.parse(await res.json());
}

/**
 * GET …/campaign-members/ladder?campaignSfdcId&playbookId[&playId] — the whole
 * first-match-wins pass: every rung's claimed members in priority order, plus
 * the members no rung claimed (`excludedMemberIds`). `params.playId` (plan
 * 2026-09-25-002, U3) buckets by that play's own ladder.
 */
export async function getMatchedMembersLadder(
  apiFetch: ApiFetch,
  params: { campaignSfdcId: string; playbookId: string; playId?: string },
): Promise<PlaybooksMatchedMembersLadderResponse> {
  const search = new URLSearchParams({
    campaignSfdcId: params.campaignSfdcId,
    playbookId: params.playbookId,
  });
  if (params.playId !== undefined) search.set('playId', params.playId);
  const res = await apiFetch(`/api/tools/playbooks/campaign-members/ladder?${search.toString()}`);
  return PlaybooksMatchedMembersLadderResponseSchema.parse(await res.json());
}

/** GET /api/tools/playbooks/plays?campaignSfdcId=… */
export async function getPlays(
  apiFetch: ApiFetch,
  params: { campaignSfdcId: string },
): Promise<PlaybooksPlaysResponse> {
  const url = new URL('/api/tools/playbooks/plays', 'http://x');
  url.searchParams.set('campaignSfdcId', params.campaignSfdcId);
  const res = await apiFetch(url.pathname + url.search);
  return PlaybooksPlaysResponseSchema.parse(await res.json());
}

/**
 * GET /api/tools/playbooks/plays/library — every live play, grouped by
 * campaign (Library "Plays" section) — plus `playbookNames` for
 * archived-playbook name resolution (F7).
 */
export async function getPlaysLibrary(apiFetch: ApiFetch): Promise<PlaybooksPlaysLibraryResponse> {
  const res = await apiFetch('/api/tools/playbooks/plays/library');
  return PlaybooksPlaysLibraryResponseSchema.parse(await res.json());
}

// ─────────────────────────────────────────────────────────────────────────────
// Admin settings (`playbooks_settings`, playbooks-clay-wire-gaps plan, Unit 2)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * GET /api/tools/playbooks/settings — the admin Settings page's read (Unit 3).
 * No admin gate on the server; any authenticated Playbooks user can read it.
 */
export async function getPlaybooksSettings(apiFetch: ApiFetch): Promise<PlaybooksSettingsResponse> {
  const res = await apiFetch('/api/tools/playbooks/settings');
  return PlaybooksSettingsResponseSchema.parse(await res.json());
}

/**
 * PATCH /api/tools/playbooks/settings — writes the Clay routine id
 * `resolvePlaybooksClayRoutineId` resolves at send time. Launcher-admin-gated
 * server-side; a non-admin caller's `apiFetch` throws a 403 `FetchErrorLike`
 * before this returns. `clayRoutineId: null` clears it back to the webhook
 * transport.
 */
export async function updatePlaybooksSettings(
  apiFetch: ApiFetch,
  body: PlaybooksUpdateSettingsRequest,
): Promise<PlaybooksSettingsResponse> {
  const res = await apiFetch('/api/tools/playbooks/settings', {
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify(body),
  });
  return PlaybooksSettingsResponseSchema.parse(await res.json());
}
